import { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

// Display noun for reference sources: the TICKET enum value is the upstream
// customer_ticket, presented as "product ticket" (concept rename).
const display_source = (s) => (s === 'TICKET' ? 'PRODUCT TICKET' : s);

/**
 * Expands one citation (Reference shape from a Finding's evidence[] array,
 * or a linkedActivityIds / linkedTicketIds entry) into the full source.
 *
 * Calls the unified resolver: POST /api/v1/references/resolve. The resolver
 * switches on `source` — Ingestion lookup for ingested kinds (WhatsApp /
 * Meet transcript / Drive note), live-query against infurnia_main for live
 * kinds (Ticket / CS_ACTIVITY) — and returns a uniform ResolvedReference.
 *
 * Why a modal rather than expand-in-place: source content can be long
 * (Meet transcripts, long ticket bodies) and we don't want to inflate the
 * surrounding card.
 */
const EvidenceModal = ({ open, on_close, store_id, evidence }) => {
    const [loading, set_loading] = useState(false);
    const [err, set_err] = useState(null);
    const [resolved, set_resolved] = useState(null);
    // Conversation context (±60min around a WhatsApp anchor). Only loaded
    // when the resolved source is WHATSAPP; everything else just shows the
    // single source body.
    const [waContext, set_wa_context] = useState([]);

    useEffect(() => {
        if (!open || !evidence) {
            set_resolved(null);
            set_wa_context([]);
            set_err(null);
            return;
        }
        const load = async () => {
            set_loading(true);
            set_err(null);
            try {
                const ref = referenceFromEvidence(evidence);
                if (!ref) {
                    set_resolved(null);
                    return;
                }
                const resp = await cs_analysis_fetch({
                    path: '/api/v1/references/resolve',
                    method: 'POST',
                    body: { refs: [ref] },
                });
                const r = resp && Array.isArray(resp.resolved) ? resp.resolved[0] : null;
                set_resolved(r);
                // WhatsApp gets the ±60min surrounding-conversation treatment
                // so reps see flow, not isolated quotes.
                if (r && r.source === 'WHATSAPP' && r.occurredAtResolved) {
                    const ctx = await cs_analysis_fetch({
                        path: `/api/v1/clients/${encodeURIComponent(store_id)}/ingestions`,
                        query: {
                            source: 'WHATSAPP',
                            around: r.occurredAtResolved,
                            windowMin: 60,
                            limit: 200,
                        },
                    });
                    set_wa_context(Array.isArray(ctx) ? ctx : []);
                }
            } catch (e) {
                set_err(e.message || String(e));
            } finally {
                set_loading(false);
            }
        };
        load();
    }, [open, evidence && (evidence.ref || evidence.ingestionId || evidence.activityId || evidence.ticketId), store_id]);

    if (!open || !evidence) return null;

    const quote = evidence.quote;
    const occurredAt = (resolved && resolved.occurredAtResolved) || evidence.occurredAt;

    return (
        <Modal isOpen={open} toggle={on_close} size='lg' scrollable>
            <ModalHeader toggle={on_close}>
                <span style={{ fontSize: 13, color: '#7f8c8d', fontWeight: 400, marginRight: 8 }}>
                    Evidence:
                </span>
                <Badge color='info' style={{ marginRight: 4 }}>
                    {display_source((resolved && resolved.source) || evidence.source) || 'source'}
                </Badge>
                {occurredAt ? (
                    <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 400 }}>
                        {fmt_datetime(occurredAt)}
                    </span>
                ) : null}
                {resolved && resolved.url ? (
                    <a
                        href={resolved.url}
                        target='_blank'
                        rel='noreferrer noopener'
                        style={{ fontSize: 12, marginLeft: 8 }}
                    >
                        open in source ↗
                    </a>
                ) : null}
            </ModalHeader>
            <ModalBody>
                {quote ? (
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff8e1', borderLeft: '3px solid #f1c40f', fontStyle: 'italic', fontSize: 13 }}>
                        “{quote}”
                    </div>
                ) : null}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spinner size='sm' /></div>
                ) : err ? (
                    <div style={{ color: '#c0392b' }}>{err}</div>
                ) : !resolved ? (
                    <div style={{ color: '#7f8c8d', fontSize: 13 }}>
                        No source linked to this evidence — may be analyzer-derived from usage data.
                    </div>
                ) : resolved.notFound ? (
                    <div style={{ color: '#c0392b', fontSize: 13 }}>
                        Source not found upstream: {resolved.title}
                    </div>
                ) : resolved.source === 'WHATSAPP' && waContext.length > 0 ? (
                    <WhatsAppConversation anchorId={resolved.ref} stream={waContext} />
                ) : (
                    <ResolvedView resolved={resolved} />
                )}
            </ModalBody>
        </Modal>
    );
};

/**
 * Convert the citation entries the analyzer / link helpers emit into a
 * canonical Reference. Tolerates the legacy field names (ingestionId,
 * activityId, ticketId) and the new unified shape ({source, ref}).
 */
function referenceFromEvidence(e) {
    if (!e) return null;
    if (e.source && e.ref) return { source: e.source, ref: e.ref, quote: e.quote, occurredAt: e.occurredAt };
    if (e.ingestionId) {
        // Old shape: source was a free string like "WHATSAPP". Pass through.
        return { source: e.source || 'WHATSAPP', ref: e.ingestionId, quote: e.quote, occurredAt: e.occurredAt };
    }
    if (e.activityId) return { source: 'CS_ACTIVITY', ref: e.activityId, quote: e.quote, occurredAt: e.occurredAt };
    if (e.ticketId)   return { source: 'TICKET',      ref: e.ticketId,   quote: e.quote, occurredAt: e.occurredAt };
    if (e.repInputId) return { source: 'REP_INPUT',   ref: e.repInputId, quote: e.quote, occurredAt: e.occurredAt };
    return null;
}

/** Source-agnostic render for any ResolvedReference. */
const ResolvedView = ({ resolved }) => {
    const meta = resolved.metadata || {};
    const metaPairs = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && v !== '');
    return (
        <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#2c3e50', marginBottom: 6 }}>
                {resolved.title}
            </div>
            {metaPairs.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {metaPairs.map(([k, v]) => (
                        <Badge key={k} color='light' style={{ color: '#2c3e50', fontWeight: 400, fontSize: 11 }}>
                            {k}: {String(v)}
                        </Badge>
                    ))}
                </div>
            ) : null}
            {resolved.body ? (
                <pre style={{ background: '#fafbfc', padding: 12, fontSize: 12, maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap', borderRadius: 4 }}>
                    {resolved.body}
                </pre>
            ) : (
                <div style={{ color: '#7f8c8d', fontSize: 12 }}>(no body)</div>
            )}
        </div>
    );
};

/**
 * WhatsApp gets a richer render — the ±60min conversation around the cited
 * message, with the anchor message highlighted. Stream is the raw Ingestion
 * rows from /clients/:id/ingestions; that endpoint hasn't moved to the
 * Reference shape so we still read .payload here directly.
 */
const WhatsAppConversation = ({ anchorId, stream }) => (
    <div>
        <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            WhatsApp · ±60min context ({stream.length} msgs)
        </div>
        <div style={{ maxHeight: 480, overflowY: 'auto', border: '1px solid #ecf0f1', borderRadius: 4 }}>
            {stream.map((m) => {
                const pp = m.payload || {};
                const me = pp.from_me;
                const isAnchor = m.id === anchorId;
                return (
                    <div
                        key={m.id}
                        style={{
                            padding: '6px 10px',
                            borderLeft: isAnchor ? '3px solid #f1c40f' : '3px solid transparent',
                            background: isAnchor ? '#fff8e1' : (me ? '#f4f6f8' : '#fff'),
                            fontSize: 12,
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#7f8c8d', fontSize: 10 }}>
                            <span>{me ? 'us' : (pp.sender_name || pp.from || pp.sender_phone || 'client')}</span>
                            <span>{fmt_datetime(m.occurredAt)}</span>
                        </div>
                        <div style={{ color: '#2c3e50', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                            {pp.body || <span style={{ color: '#bdc3c7' }}>—</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

export default EvidenceModal;
